import moment from 'moment';

const supportedDateFormats = [
  'YYYY-MM-DD',
  'MM/DD/YYYY',
  'M/DD/YYYY',
  'DD/MM/YYYY',
  'DD/M/YYYY',
  'YYYY/MM/DD',
  'MMMM DD, YYYY',
  'MMMM D, YYYY',
  'MMM DD YYYY',
  'MMM D YYYY',
  'MMMDDYYYY',
  'DD MMM YYYY',
  'D MMM YYYY',
  'DDMMMYYYY',
  'YYYY-MM-DDTHH:mm:ssZ',
  'YYYY-MM-DDTHH:mm:ss.SSSSSSZ',
];

const supportedDateFormatsWithoutYear = [
  'MMMM DD',
  'MMMM D',
  'DD MMM',
  'D MMM',
  'MMM DD',
  'MMM D',
  'MMMDD',
  'MMMD',
  'DMMM',
  'DDMMM',
];

/**
 * Formats a date string into a specified format.
 *
 * @param {string} str - The date string to format.
 * @param {string} [format='MMM DD, YYYY'] - The format to use if the year is included in the date string.
 * @param {string} [formatWithoutYear='MMM DD'] - The format to use if the year is not included in the date string.
 * @returns {string} The formatted date string.
 */
export const formatDate = (str, format = 'MMM DD, YYYY', formatWithoutYear = 'MMM DD') => {
  if (!str) return str;

  // Try to parse the date string using the supported date formats.
  const date = supportedDateFormats.reduce((dateOutput, dateFormat) => {
    if (dateOutput?.isValid()) return dateOutput; // If a valid date is already found, return it.
    const d = moment.utc(str, dateFormat, true); // Parse the date string with the current format.
    if (d.isValid()) return d; // If the parsed date is valid, return it.
    return null; // Otherwise, continue to the next format.
  }, null);

  // If a valid date is found, format it using the specified format.
  if (date) return date.format(format);

  // Try to parse the date string using the supported formats that do not include the year.
  const dateWithoutYear = supportedDateFormatsWithoutYear.reduce((dateOutput, dateFormat) => {
    if (dateOutput?.isValid()) return dateOutput; // If a valid date is already found, return it.
    const d = moment.utc(str, dateFormat, true); // Parse the date string with the current format.
    if (d.isValid()) return d; // If the parsed date is valid, return it.
    return null; // Otherwise, continue to the next format.
  }, null);

  // If a valid date without a year is found, format it using the formatWithoutYear.
  if (dateWithoutYear) return dateWithoutYear.format(formatWithoutYear);

  // If no valid date is found, return null.
  return null;
};

export const getYearList = () => {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 11 }).map((_, yearIncrement) => ({
    id: currentYear + yearIncrement,
    description: `${currentYear + yearIncrement}`,
  }));
  return years;
};

export const getMonthList = () => {
  const months = Array.from({ length: 12 }).map((_, monthNumber) => ({
    id: monthNumber + 1,
    description: `${monthNumber + 1}`,
  }));
  return months;
};

export const getAllMonths = () => [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Parses a date string and returns it in ISO format (YYYY-MM-DD).
 * If the input string does not contain a valid date, it attempts to parse it using a dd/mm/yyyy format.
 *
 * @param {string} str - The date string to be parsed.
 * @returns {string} - The parsed date string in ISO format or the original string if parsing fails.
 */
export const parseDateToISOString = (str) => {
  const dateStr = formatDate(str, 'YYYY-MM-DD', 'MM-DD');

  if (dateStr && dateStr !== 'Invalid date' && dateStr !== 'Invalid Date' && dateStr !== 'Invalid') return dateStr;

  return null;
};

export const parseDateFromSearchText = (searchText) => parseDateToISOString(searchText) || searchText;

export const getOrderHistoryFilterDates = (startDateStr, endDateStr) => {
  const startDate = moment.utc(startDateStr);
  const endDate = moment.utc(endDateStr);
  endDate.add(1, 'days');
  return [startDate.format('MM/DD/YYYY'), endDate.format('MM/DD/YYYY')];
};
